import { useReactiveVar } from '@apollo/client'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { ChangeEvent, KeyboardEvent, useState } from 'react'
import PasswordVisibilityAdornment from '../../../../components/forms/PasswordVisibilityAdornment'
import Loading from '../../../../components/PageLoading'
import { BucketShareStatus } from '../../../../graphql/generated'
import { publicPasswordValidatedVar } from '../../../../providers/apollo/cache'
import userBucketSharePasswordValidate from '../../../buckets/hooks/useBucketSharePasswordValidate'
import useBucketShareByIdProtected from '../../hooks/useBucketShareByIdProtected'

export default function PasswordPrompt() {
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState('')
  const { shareProtectedDetails, loading } = useBucketShareByIdProtected()
  const { mutation } = userBucketSharePasswordValidate()
  const publicPasswordValidated = useReactiveVar(publicPasswordValidatedVar)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const shareId = shareProtectedDetails?.id

  const handleSubmit = () => {
    if (!shareId || !password) return
    mutation({ variables: { shareId, password } })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword)

  const bucketShare = shareProtectedDetails
  const bucketShareLoading = loading

  if (bucketShareLoading) {
    return <Loading />
  }

  if (!bucketShare) {
    return null
  }

  if (bucketShare.shareStatus === BucketShareStatus.Expired) {
    return (
      <Typography align="center" variant="h5" sx={{ marginTop: 12 }}>
        This public share has expired
      </Typography>
    )
  }

  let borderColor
  if (publicPasswordValidated === false) {
    borderColor = 'error.main'
  } else if (publicPasswordValidated === true) {
    borderColor = 'success.main'
  }

  return (
    <Dialog open maxWidth="sm" fullWidth onKeyDown={handleKeyDown}>
      <DialogTitle color="primary">Enter password to view Sharing Link</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, marginTop: 1 }}>
          <TextField
            sx={{ marginTop: 1 }}
            label="Password"
            name="password"
            value={password}
            fullWidth
            onChange={handleChange}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: <PasswordVisibilityAdornment showPassword={showPassword} handleClickShowPassword={handleClickShowPassword} />,
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor
                }
              }
            }}
          />
        </Box>
        {publicPasswordValidated === false && (
          <Typography color="error" sx={{ marginTop: 1 }}>
            Invalid password. Please try again.
          </Typography>
        )}
      </DialogContent>
      <DialogActions style={{ marginTop: '12px' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            window.location.href = '/auth/login/'
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={password.length < 1} onClick={handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}
